import React from "react";
import PropTypes from "prop-types";
import "./CompositionBar.scss";

/**
 * CompositionBar — one bar split into its parts, with a legend that carries the
 * numbers. For "what is this made of": channel mix, traffic quality, device split.
 *
 * Segments under `minShare` fold into "Other" so the bar stays readable; the legend still
 * lists them when `showAll` is set.
 */
const CompositionBar = ({
  segments, onSegmentClick, activeKey, height = 14, minShare = 0.01, showLegend = true, unit, format,
}) => {
  const f = format || ((n) => Number(n).toLocaleString("en-GB"));
  const total = segments.reduce((t, s) => t + (s.value || 0), 0);
  const visible = [];
  let other = 0;
  segments.forEach((s) => {
    if (!s.value) return;
    if (total && s.value / total < minShare) other += s.value;
    else visible.push(s);
  });
  if (other) visible.push({ key: "__other", label: "Other", value: other, color: "#cbd5e1" });
  const share = (v) => (total ? (v / total) * 100 : 0);
  const shareLabel = (v) => { const x = share(v); return x > 0 && x < 1 ? "<1%" : `${Math.round(x)}%`; };

  return (
    <div className="composition">
      <div className="composition__bar" style={{ height }} role="img" aria-label={visible.map((s) => `${s.label} ${shareLabel(s.value)}`).join(", ")}>
        {visible.map((s) => (
          <span
            key={s.key}
            className={`composition__seg${activeKey === s.key ? " composition__seg--active" : ""}${activeKey && activeKey !== s.key ? " composition__seg--dim" : ""}`}
            style={{ width: `${share(s.value)}%`, background: s.color }}
            title={`${s.label}: ${f(s.value)}${unit ? ` ${unit}` : ""} (${shareLabel(s.value)})`}
          />
        ))}
      </div>
      {showLegend && (
        <div className="composition__legend">
          {visible.map((s) => {
            const Tag = onSegmentClick && s.key !== "__other" ? "button" : "span";
            return (
              <Tag
                key={s.key}
                type={Tag === "button" ? "button" : undefined}
                className={`composition__item${activeKey === s.key ? " composition__item--active" : ""}`}
                onClick={Tag === "button" ? () => onSegmentClick(s.key) : undefined}
                title={s.help || undefined}
              >
                <i style={{ background: s.color }} />
                <span className="composition__name">{s.label}</span>
                <span className="composition__num">{f(s.value)}</span>
                <span className="composition__pct">{shareLabel(s.value)}</span>
              </Tag>
            );
          })}
        </div>
      )}
    </div>
  );
};

CompositionBar.propTypes = {
  segments: PropTypes.arrayOf(PropTypes.shape({
    key: PropTypes.string.isRequired, label: PropTypes.node.isRequired, value: PropTypes.number,
    color: PropTypes.string, help: PropTypes.string,
  })).isRequired,
  onSegmentClick: PropTypes.func,
  activeKey: PropTypes.string,
  height: PropTypes.number,
  minShare: PropTypes.number,
  showLegend: PropTypes.bool,
  unit: PropTypes.string,
  format: PropTypes.func,
};

export default CompositionBar;
